import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Row, Col, Select, Checkbox, Upload, Space, Spin } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import SmartEditor from '../../components/Common/SmartEditor';
import { draftService } from '../../services/draftService';

const { Dragger } = Upload;

const DraftCopy: React.FC = () => {
  const { idx } = useParams<{ idx: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sendMail, setSendMail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  const [draftNo, setDraftNo] = useState('');
  const [receivers, setReceivers] = useState<any[]>([]);

  // 기안서 데이터 로드 (복사할 원본)
  useEffect(() => {
    if (idx) {
      fetchDraftData(parseInt(idx));
      fetchInitData();
    }
  }, [idx]);

  const fetchDraftData = async (draftIdx: number) => {
    setDataLoading(true);
    try {
      const response = await draftService.getDraftDetail(draftIdx);
      console.log('📥 복사할 기안서 데이터:', response);
      if (response.resultCode === 200) {
        const data = response.data.info;
        console.log('📄 원본 내용:', data.Draft_Contents);

        // 폼 데이터 설정 (원본 복사, 수신자는 비워둠)
        form.setFieldsValue({
          Draft_Subject: `[복사] ${data.Draft_Subject}`,
          Draft_Cost: data.Draft_Cost,
          Draft_Client: data.Draft_Client,
          Receiver: '', // 수신자는 다시 선택해야 함
        });

        setSendMail(data.Send_Mail_YN === 'Y' || data.Send_Mail_YN === 1);
        setSendSMS(data.Send_SMS_YN === 'Y' || data.Send_SMS_YN === 1);

        // HTML 엔티티 디코딩
        const decodeHTML = (html: string) => {
          const txt = document.createElement('textarea');
          txt.innerHTML = html;
          return txt.value;
        };

        // 스마트에디터에 내용 설정 (디코딩 적용)
        const setEditorContent = () => {
          console.log('⏰ 에디터 설정 시도...');
          console.log('🔍 oEditors:', (window as any).oEditors);

          if ((window as any).oEditors && (window as any).oEditors[0]) {
            // 먼저 에디터 내용 초기화
            (window as any).oEditors[0].exec('SET_IR', ['']);

            if (data.Draft_Contents) {
              const decodedContent = decodeHTML(data.Draft_Contents);
              console.log('✅ 디코딩된 내용:', decodedContent.substring(0, 100) + '...');

              // 내용 설정
              setTimeout(() => {
                (window as any).oEditors[0].exec('SET_IR', [decodedContent]);
                console.log('✅ 기안서 복사 내용 로드 완료');
              }, 300);
            }
          } else {
            console.warn('⚠️ 에디터가 아직 준비되지 않음, 재시도...');
            setTimeout(setEditorContent, 500);
          }
        };

        setTimeout(setEditorContent, 2000);
      }
    } catch (error) {
      console.error('기안서 조회 오류:', error);
      message.error('기안서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchInitData = async () => {
    try {
      const result = await draftService.getDraftInit();
      console.log('초기 데이터 응답:', result);
      if (result.resultCode === 200) {
        setDraftNo(result.data.draftNo);
        setReceivers(result.data.receivers || []);
        console.log('수신 결재자:', result.data.receivers);
      }
    } catch (error) {
      console.error('초기 데이터 로드 오류:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // 스마트에디터 내용 추출
      const content = (window as any).getContent_smarteditor?.();

      const formData = new FormData();
      formData.append('Draft_No', draftNo);
      formData.append('Draft_Subject', values.Draft_Subject);
      formData.append('Draft_Cost', values.Draft_Cost);
      formData.append('Draft_Client', values.Draft_Client);
      formData.append('Receiver', values.Receiver || '');
      formData.append('editorTxt', content || '');
      formData.append('Send_Mail_YN', sendMail ? 'Y' : 'N');
      formData.append('Send_SMS_YN', sendSMS ? 'Y' : 'N');

      // 파일 추가
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('fileInput', file.originFileObj);
        }
      });

      console.log('📝 복사 등록 데이터:', { ...values, content, fileCount: fileList.length });

      // 기안서 등록 API 호출 (draftService 사용)
      const result = await draftService.createDraft(formData);

      if (result.resultCode === 200) {
        message.success('기안서가 복사 등록되었습니다.');
        navigate('/draft/list');
      } else {
        message.error(result.message || '기안서 등록 실패');
      }
    } catch (error) {
      console.error('기안서 복사 오류:', error);
      message.error('기안서 복사 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: (file: any) => {
      const uploadFile: UploadFile = {
        uid: `${Date.now()}-${file.name}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setFileList([...fileList, uploadFile]);
      return false;
    },
    onRemove: (file: UploadFile) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  if (dataLoading) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" tip="기안서를 불러오는 중..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Card
        title="기안서 복사"
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/draft/list')}>
            목록으로
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* 1줄: 기안번호, 수신자, 메일/SMS, 비용, 지출처 */}
          <Row gutter={16}>
            <Col span={3}>
              <Form.Item label="기안번호">
                <Input disabled value={draftNo} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="수신(결재자)"
                name="Receiver"
                rules={[{ required: true, message: '수신자 선택' }]}
              >
                <Select
                  placeholder="선택"
                  optionLabelProp="label"
                >
                  {receivers.map((r) => (
                    <Select.Option
                      key={r.Admin_Name}
                      value={`${r.Admin_Name} ${r.Admin_Position}|${r.Admin_Email}|${r.Admin_Cell}`}
                      label={`${r.Admin_Name} ${r.Admin_Position}`}
                    >
                      {r.Admin_Name} {r.Admin_Position}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="발송 옵션">
                <Space>
                  <Checkbox checked={sendMail} onChange={(e) => setSendMail(e.target.checked)}>
                    메일
                  </Checkbox>
                  <Checkbox checked={sendSMS} onChange={(e) => setSendSMS(e.target.checked)}>
                    SMS
                  </Checkbox>
                </Space>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="비용"
                name="Draft_Cost"
                rules={[{ required: true, message: '금액 필수' }]}
              >
                <Input type="number" placeholder="금액" addonAfter="원" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="지출처" name="Draft_Client">
                <Input placeholder="지출처" />
              </Form.Item>
            </Col>
          </Row>

          {/* 2줄: 제목 */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="제목"
                name="Draft_Subject"
                rules={[{ required: true, message: '제목을 입력해주세요' }]}
              >
                <Input placeholder="제목을 입력하세요" />
              </Form.Item>
            </Col>
          </Row>

          {/* 첨부파일 */}
          <Form.Item label="첨부파일">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
              <p className="ant-upload-hint">
                여러 파일을 동시에 업로드할 수 있습니다.
              </p>
            </Dragger>
          </Form.Item>

          {/* 스마트에디터 */}
          <Form.Item label="내용">
            <SmartEditor id="smarteditor" height="400px" />
          </Form.Item>

          {/* 버튼 */}
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                복사 등록
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </MainLayout>
  );
};

export default DraftCopy;
